import { useState } from 'react';
import { line, remainingToReceive, Size, t, type ReceiveRequest, type ShipConfirmationRequest } from '@bms2/shared';
import { DatePicker, NumericInput, Select, TextInput, Textarea } from '../../components/controls';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Modal } from '../../components/modals/Modal';
import modalStyles from '../../components/modals/modal.module.css';
import styles from './ConfirmationModal.module.css';
import sharedStyles from '../../components/composites/shared.module.css';
import { useDocumentToday } from '../../shell/ViewerZone';

/** A line as the Receive half lists it: what was ordered, what has arrived, what is left. */
export interface ReceivableLine {
  id: string;
  label: string;
  quantity: number;
  quantityConsumed: number;
  unitName: string;
}

export type ConfirmationMode =
  | { kind: 'receive'; lines: readonly ReceivableLine[] }
  /** The Ship half: a drop-ship order needs no origin; a shipment from stock names the site the goods left, defaulted from the dispatch origin. */
  | { kind: 'confirmShipment'; origins?: readonly { id: string; label: string }[]; defaultOriginId?: string | null };

export interface ConfirmationModalProps {
  open: boolean;
  mode: ConfirmationMode;
  /** The document the confirmation is against, for the title. */
  documentNumber: string;
  busy?: boolean;
  onCancel: () => void;
  onReceive: (request: ReceiveRequest) => void;
  onConfirmShipment: (request: ShipConfirmationRequest) => void;
}

/** The receipt lines with a quantity entered, and nothing else; a zero or a blank is a line not received this time. */
export function receiptLines(entered: Readonly<Record<string, number | null>>): ReceiveRequest['lines'] {
  return Object.entries(entered).flatMap(([lineId, quantity]) => (quantity !== null && quantity > 0 ? [{ lineId, quantity }] : []));
}

/** What the Receive window opens offering: each line's remainder, a line already received in full left out. */
export function offeredRemainders(lines: readonly ReceivableLine[]): Record<string, number | null> {
  return Object.fromEntries(lines.flatMap((row) => (remainingToReceive(row) > 0 ? [[row.id, remainingToReceive(row)]] : [])));
}

/**
 * The shared Receive/Ship Confirmation screen (piece 22; decision 0029): one
 * modal, narrow-first, whose Receive half writes a partial receipt against the
 * purchase order's lines and whose Ship half carries the date, the carrier and
 * the tracking reference — for a drop-ship order, which posts no stock, and for
 * a Sales Order shipment, which also names the site the goods left.
 */
export function ConfirmationModal({ open, mode, documentNumber, busy = false, onCancel, onReceive, onConfirmShipment }: ConfirmationModalProps) {
  // 🔒 Each open line opens offering what is left of it, so a full delivery is one press and a short one is a correction (T926 item 3).
  const [entered, setEntered] = useState<Record<string, number | null>>(() => (mode.kind === 'receive' ? offeredRemainders(mode.lines) : {}));
  const [receivedOn, setReceivedOn] = useState('');
  const [note, setNote] = useState('');
  // 🔒 Opens on today in the viewer's own zone, through the hook every document form reads (decision 1030).
  const today = useDocumentToday();
  const [shippedOn, setShippedOn] = useState(today);
  const [carrier, setCarrier] = useState('');
  const [tracking, setTracking] = useState('');
  const [origin, setOrigin] = useState<string>(mode.kind === 'confirmShipment' ? mode.defaultOriginId ?? '' : '');
  const [error, setError] = useState<string | null>(null);

  const receiving = mode.kind === 'receive';
  const needsOrigin = mode.kind === 'confirmShipment' && (mode.origins?.length ?? 0) > 0;

  const submit = () => {
    if (receiving) {
      const lines = receiptLines(entered);
      if (lines.length === 0) return setError(t('ship.receive.needsLine'));
      for (const receipt of lines) {
        const source = mode.lines.find((l) => l.id === receipt.lineId)!;
        if (receipt.quantity > remainingToReceive(source) + 1e-9) return setError(line(source.label, '·', String(remainingToReceive(source)), t('ship.receive.remaining')));
      }
      return onReceive({ lines, receivedOn: receivedOn || null, note: note.trim() || null });
    }
    if (!shippedOn) return setError(t('ship.confirm.needsDate'));
    if (!tracking.trim()) return setError(t('ship.confirm.needsTracking'));
    if (needsOrigin && !origin) return setError(t('ship.confirm.needsOrigin'));
    return onConfirmShipment({ shippedOn, carrier: carrier.trim() || null, trackingNumber: tracking.trim(), originSiteId: needsOrigin ? origin : null });
  };

  return (
    <Modal
      open={open}
      title={line(receiving ? t('ship.receive.title') : t('ship.confirm.title'), '·', documentNumber)}
      onCancel={onCancel}
      dataModal={receiving ? 'receive' : 'confirm-shipment'}
      actions={[
        { kind: 'primary', label: receiving ? t('ship.receive.save') : t('ship.confirm.save'), onAct: submit, disabled: busy },
        { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel },
      ]}
    >
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      {receiving ? (
        <>
          <p className={modalStyles.fieldLabel}>{t('ship.receive.hint')}</p>
          <ul className={`${sharedStyles.listReset} ${styles.lines}`} data-receive-lines={mode.lines.length}>
            {mode.lines.map((row) => {
              const remaining = remainingToReceive(row);
              return (
                <li key={row.id} className={styles.line} data-receive-line={row.id} data-receive-remaining={remaining}>
                  <span className={styles.label}>
                    <span className={styles.name}>{row.label}</span>
                    <span className={styles.remaining}>{line(String(row.quantityConsumed), '/', String(row.quantity), row.unitName, '·', String(remaining), t('ship.receive.remaining'))}</span>
                  </span>
                  <NumericInput size={Size.Small}
                    label={t('ship.receive.quantity')}
                    kind="number"
                    decimals={2}
                    value={entered[row.id] ?? null}
                    onChange={(next) => setEntered((was) => ({ ...was, [row.id]: next }))}
                    name={`receive-${row.id}`}
                    disabled={remaining <= 0}
                  />
                </li>
              );
            })}
          </ul>
          <div className={modalStyles.pair}>
            <DatePicker size={Size.Small} label={t('ship.receive.date')} value={receivedOn} onChange={setReceivedOn} mode="date" name="receivedOn" />
          </div>
          <Textarea size={Size.Large} label={t('ship.receive.note')} value={note} onChange={setNote} />
        </>
      ) : (
        <>
          <p className={modalStyles.fieldLabel}>{needsOrigin ? t('ship.confirm.hintStock') : t('ship.confirm.hint')}</p>
          <div className={modalStyles.pair}>
            <DatePicker size={Size.Small} label={t('ship.confirm.shippedOn')} value={shippedOn} onChange={setShippedOn} mode="date" name="shippedOn" required />
            <TextInput size={Size.Medium} label={t('ship.confirm.carrier')} value={carrier} onChange={setCarrier} name="carrier" />
          </div>
          <TextInput size={Size.Medium} label={t('ship.confirm.tracking')} value={tracking} onChange={setTracking} name="trackingNumber" required />
          {needsOrigin ? (
            <Select size={Size.Medium} label={t('ship.confirm.origin')} required value={origin} onChange={setOrigin} placeholder={t('customFieldsModal.none')} options={(mode.kind === 'confirmShipment' ? mode.origins ?? [] : []).map((site) => ({ value: site.id, label: site.label }))} dataAttribute={{ 'data-confirm-origin': 'true' }} />
          ) : null}
        </>
      )}
    </Modal>
  );
}
